import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { isInvalidMapping } from '~/components/Admin/IntegrationPage/util';
import { Select } from '~/eds/src/components/Select/Select';
import { IconType } from '~/eds/src/types/IconType';
import { Option } from '~/eds/src/types/Option';
import { UserAction } from '~/eds/src/types/UserAction';
import { AribaFieldSubType } from '~/enums/AribaFieldSubType';
import { DataFieldType } from '~/enums/DataFieldType';
import { api } from '~/redux/api/createApi';
import { AribaDataField } from '~/types/Field';
import { Nullable } from '~/types/Nullable';
import { sortByStringValue } from '~/utils/array';

type Props = {
  value: string;
  syncpair: string;
  evisortFieldType: DataFieldType;
  onChange: (field: Nullable<AribaDataField>) => void;
  disabled?: boolean;
  footerAction?: UserAction;
  filterOption?: (option: Option<string>) => boolean;
  isLoading?: boolean;
  name?: string;
  mapOption?: (
    option: Option<string, AribaDataField>,
  ) => Option<string, AribaDataField>;
  sortOption?: (
    a: Option<string, AribaDataField>,
    b: Option<string, AribaDataField>,
  ) => number;
};

export const AribaFieldSelect = ({
  disabled,
  filterOption,
  footerAction,
  onChange,
  name = 'field-select',
  sortOption = sortByStringValue('label'),
  value,
  syncpair,
  evisortFieldType,
}: Props) => {
  const intl = useIntl();
  const {
    data: aribaFields,
    isLoading,
    error: aribaFieldsError,
  } = api.endpoints.getAribaFields.useQuery(syncpair);
  const error = aribaFieldsError
    ? intl.formatMessage({
        id: 'AribaFieldSelect.unableToLoadFields',
        defaultMessage: 'Unable to load fields',
        description: 'Error message when Ariba fields cannot be loaded',
      })
    : undefined;

  const getIsDisabled = (aribaFieldType: string) => {
    return isInvalidMapping(evisortFieldType, aribaFieldType);
  };

  const options = useMemo(() => {
    const wsFields = [...(aribaFields?.workspace ?? [])];
    return [
      ...wsFields
        .map((field) => {
          return {
            ...field,
            primitiveType: getAribaPrimitiveType(field),
          };
        })
        .map((field) => {
          return {
            data: field,
            value: field.fieldId,
            label: field.fieldName,
            leftIcon: getIconFromFieldType(field.primitiveType),
            disabled: getIsDisabled(field.primitiveType),
          };
        }),
    ].sort(sortOption);
  }, [aribaFields, sortOption]);

  const optionsMap: Record<string, AribaDataField> = useMemo(
    () =>
      Object.fromEntries(options.map((option) => [option.value, option.data!])),
    [options],
  );

  const handleChange = (value: Nullable<string>) => {
    onChange(value === null ? null : optionsMap[value]);
  };

  return (
    <Select
      disabled={disabled}
      error={error}
      filterOption={filterOption}
      footerAction={footerAction}
      isClearable={false}
      isLoading={isLoading}
      isMulti={false}
      name={name}
      options={options}
      placeholder={intl.formatMessage({
        id: 'AribaFieldSelect.searchForFieldPlaceholder',
        defaultMessage: 'Search for a field…',
        description: 'Placeholder for Ariba field select input',
      })}
      value={value}
      onChange={handleChange}
    />
  );
};

const getIconFromFieldType = (type: string): IconType => {
  switch (type) {
    case AribaFieldSubType.BooleanValue:
      return 'field-boolean';
    case AribaFieldSubType.DateValue:
      return 'field-date';
    case AribaFieldSubType.TextValue:
      return 'field-short-response';
    case AribaFieldSubType.TextMultiSelectValue:
      return 'field-multiple-choice';
    case AribaFieldSubType.BigDecimalValue:
    case AribaFieldSubType.IntValue:
    case AribaFieldSubType.NumberValue:
      return 'field-number';
    case AribaFieldSubType.MultiLineTextValue:
      return 'field-paragraph';
    default:
      return 'field';
  }
};

const getAribaPrimitiveType = (field: AribaDataField): string => {
  switch (field.fieldDataSubType) {
    case AribaFieldSubType.MoneyValue:
    case AribaFieldSubType.UserMultiSelectValue:
      switch (field.fieldDataType) {
        case 'number':
          return AribaFieldSubType.NumberValue;
        case 'string':
          return AribaFieldSubType.TextValue;
        case 'date':
          return AribaFieldSubType.DateValue;
        default:
          return field.fieldDataSubType;
      }
    default:
      return field.fieldDataSubType;
  }
};
